import { useMemo } from "react";

const TIERS = {
  1: { name: "Gray",     color: "#888888", bg: "#2a2a2a", accent: "#888888", border: 2 },
  2: { name: "Blue",     color: "#3a6ab0", bg: "#0d2240", accent: "#5a8ad0", border: 3 },
  3: { name: "Green",    color: "#2e7d32", bg: "#0a2010", accent: "#4caf50", border: 4 },
  4: { name: "Purple",   color: "#6a1b9a", bg: "#120820", accent: "#ab47bc", border: 5 },
  5: { name: "Red",      color: "#c62828", bg: "#1a0505", accent: "#ef5350", border: 6 },
  6: { name: "Bronze",   color: "#8d5524", bg: "#110800", accent: "#daa520", border: 7 },
  7: { name: "Silver",   color: "#9e9e9e", bg: "#111118", accent: "#e0e0e0", border: 8 },
  8: { name: "Gold",     color: "#daa520", bg: "#100800", accent: "#ffd700", border: 10 },
  9: { name: "Platinum", color: "#c0c0e8", bg: "#080810", accent: "#e8e8ff", border: 10 },
};

function Stamps({ earned, required, accent, color }) {
  const cols = Math.min(required, 5);
  const stamps = [];
  const sx = 50, sy = 80, sp = 48;
  for (let i = 0; i < required; i++) {
    const cx = sx + (i % cols) * sp;
    const cy = sy + Math.floor(i / cols) * sp;
    stamps.push(
      i < earned
        ? <circle key={i} cx={cx} cy={cy} r="14" fill={accent} opacity="0.9" />
        : <circle key={i} cx={cx} cy={cy} r="14" fill="none" stroke={color} strokeWidth="2" opacity="0.3" />
    );
  }
  return <>{stamps}</>;
}

function BorderLevel1({ w, h }) {
  return <rect x="1" y="1" width={w-2} height={h-2} rx="0" fill="none" stroke="#888888" strokeWidth="2" />;
}

function BorderLevel2({ w, h }) {
  const c = "#3a6ab0", c2 = "#5a8ad0";
  return <>
    <rect x="1.5" y="1.5" width={w-3} height={h-3} rx="2" fill="none" stroke={c} strokeWidth="3" />
    <rect x="8" y="8" width={w-16} height={h-16} rx="1" fill="none" stroke={c2} strokeWidth="1.5" />
    {/* Corner ticks */}
    <line x1="0" y1="12" x2="12" y2="12" stroke={c2} strokeWidth="1.5" />
    <line x1="12" y1="0" x2="12" y2="12" stroke={c2} strokeWidth="1.5" />
    <line x1={w-12} y1="0" x2={w-12} y2="12" stroke={c2} strokeWidth="1.5" />
    <line x1={w} y1="12" x2={w-12} y2="12" stroke={c2} strokeWidth="1.5" />
    <line x1="0" y1={h-12} x2="12" y2={h-12} stroke={c2} strokeWidth="1.5" />
    <line x1="12" y1={h} x2="12" y2={h-12} stroke={c2} strokeWidth="1.5" />
    <line x1={w-12} y1={h} x2={w-12} y2={h-12} stroke={c2} strokeWidth="1.5" />
    <line x1={w} y1={h-12} x2={w-12} y2={h-12} stroke={c2} strokeWidth="1.5" />
  </>;
}

function BorderLevel3({ w, h }) {
  const c = "#2e7d32", c2 = "#4caf50", c3 = "#1b5e20";
  return <>
    <rect x="2" y="2" width={w-4} height={h-4} rx="2" fill="none" stroke={c} strokeWidth="4" />
    <rect x="9" y="9" width={w-18} height={h-18} rx="1" fill="none" stroke={c2} strokeWidth="1" />
    <rect x="13" y="13" width={w-26} height={h-26} rx="1" fill="none" stroke={c3} strokeWidth="1" />
    {/* Corner rosettes */}
    {[[12,12],[w-12,12],[12,h-12],[w-12,h-12]].map(([cx,cy],i) =>
      <circle key={i} cx={cx} cy={cy} r="5" fill={c2} opacity="0.7" />
    )}
  </>;
}

function BorderLevel4({ w, h }) {
  const c = "#6a1b9a", c2 = "#ab47bc";
  return <>
    <rect x="2.5" y="2.5" width={w-5} height={h-5} rx="3" fill="none" stroke={c} strokeWidth="5" />
    <rect x="10" y="10" width={w-20} height={h-20} rx="2" fill="none" stroke={c2} strokeWidth="1" />
    {/* Corner arcs + circles */}
    <path d={`M 5 40 Q 5 5 40 5`} fill="none" stroke={c2} strokeWidth="2" />
    <path d={`M ${w-40} 5 Q ${w-5} 5 ${w-5} 40`} fill="none" stroke={c2} strokeWidth="2" />
    <path d={`M 5 ${h-40} Q 5 ${h-5} 40 ${h-5}`} fill="none" stroke={c2} strokeWidth="2" />
    <path d={`M ${w-40} ${h-5} Q ${w-5} ${h-5} ${w-5} ${h-40}`} fill="none" stroke={c2} strokeWidth="2" />
    {[[12,12],[w-12,12],[12,h-12],[w-12,h-12]].map(([cx,cy],i) =>
      <g key={i}>
        <circle cx={cx} cy={cy} r="6" fill={c} />
        <circle cx={cx} cy={cy} r="3" fill={c2} opacity="0.8" />
      </g>
    )}
  </>;
}

function BorderLevel5({ w, h }) {
  const c = "#c62828", c2 = "#ef5350", c3 = "#b71c1c";
  return <>
    <rect x="3" y="3" width={w-6} height={h-6} rx="3" fill="none" stroke={c} strokeWidth="6" />
    <rect x="12" y="12" width={w-24} height={h-24} rx="2" fill="none" stroke={c2} strokeWidth="1" />
    <rect x="15" y="15" width={w-30} height={h-30} rx="1" fill="none" stroke={c3} strokeWidth="0.5" />
    {/* Corner wedge + leaf shapes */}
    {[[0,0,1,1],[1,0,-1,1],[0,1,1,-1],[1,1,-1,-1]].map(([fx,fy,dx,dy],i) => {
      const ox = fx ? w-5 : 5, oy = fy ? h-5 : 5;
      return <g key={i}>
        <path d={`M ${ox} ${oy} L ${ox+dx*20} ${oy} L ${ox} ${oy+dy*20} Z`} fill={c3} opacity="0.6" />
        <path d={`M ${ox+dx*3} ${oy+dy*3} Q ${ox+dx*15} ${oy+dy*3} ${ox+dx*18} ${oy+dy*18}`} fill="none" stroke={c2} strokeWidth="1.5" opacity="0.5" />
        <circle cx={ox+dx*3} cy={oy+dy*3} r="4" fill={c} />
        <circle cx={ox+dx*3} cy={oy+dy*3} r="2" fill={c2} />
      </g>;
    })}
  </>;
}

function BorderLevel6({ w, h }) {
  const c = "#8d5524", c2 = "#daa520", c3 = "#5c3a00";
  return <>
    <rect x="3.5" y="3.5" width={w-7} height={h-7} rx="3" fill="none" stroke={c} strokeWidth="7" />
    <rect x="13" y="13" width={w-26} height={h-26} rx="2" fill="none" stroke={c2} strokeWidth="1.5" />
    <rect x="17" y="17" width={w-34} height={h-34} rx="1" fill="none" stroke={c3} strokeWidth="1" />
    {/* Corner acanthus clusters */}
    {[[0,0,1,1],[1,0,-1,1],[0,1,1,-1],[1,1,-1,-1]].map(([fx,fy,dx,dy],i) => {
      const ox = fx ? w-8 : 8, oy = fy ? h-8 : 8;
      return <g key={i}>
        <ellipse cx={ox+dx*12} cy={oy+dy*5} rx="10" ry="5" fill={c} opacity="0.5" transform={`rotate(${fx===fy?-30:30} ${ox+dx*12} ${oy+dy*5})`} />
        <ellipse cx={ox+dx*5} cy={oy+dy*12} rx="10" ry="5" fill={c2} opacity="0.4" transform={`rotate(${fx===fy?60:-60} ${ox+dx*5} ${oy+dy*12})`} />
        <ellipse cx={ox+dx*10} cy={oy+dy*10} rx="8" ry="4" fill={c3} opacity="0.5" transform={`rotate(${fx===fy?15:-15} ${ox+dx*10} ${oy+dy*10})`} />
        <circle cx={ox+dx*4} cy={oy+dy*4} r="5" fill={c2} />
        <circle cx={ox+dx*4} cy={oy+dy*4} r="2.5" fill="#fff" opacity="0.3" />
      </g>;
    })}
    {/* Side medallions */}
    <ellipse cx="5" cy={h/2} rx="4" ry="8" fill={c} opacity="0.4" />
    <ellipse cx={w-5} cy={h/2} rx="4" ry="8" fill={c} opacity="0.4" />
  </>;
}

function BorderLevel7({ w, h }) {
  const c = "#9e9e9e", c2 = "#e0e0e0", c3 = "#757575";
  return <>
    <rect x="4" y="4" width={w-8} height={h-8} rx="3" fill="none" stroke={c} strokeWidth="8" />
    <rect x="14" y="14" width={w-28} height={h-28} rx="2" fill="none" stroke={c2} strokeWidth="1" />
    <rect x="17" y="17" width={w-34} height={h-34} rx="1" fill="none" stroke={c3} strokeWidth="0.5" />
    {/* Corner S-curve scrolls + leaf sprays */}
    {[[0,0,1,1],[1,0,-1,1],[0,1,1,-1],[1,1,-1,-1]].map(([fx,fy,dx,dy],i) => {
      const ox = fx ? w-5 : 5, oy = fy ? h-5 : 5;
      return <g key={i}>
        <path d={`M ${ox} ${oy+dy*25} C ${ox+dx*10} ${oy+dy*25} ${ox+dx*10} ${oy} ${ox+dx*25} ${oy}`} fill="none" stroke={c2} strokeWidth="2" opacity="0.6" />
        <ellipse cx={ox+dx*15} cy={oy+dy*8} rx="8" ry="4" fill={c} opacity="0.3" transform={`rotate(${fx===fy?-25:25} ${ox+dx*15} ${oy+dy*8})`} />
        <ellipse cx={ox+dx*8} cy={oy+dy*15} rx="8" ry="4" fill={c3} opacity="0.3" transform={`rotate(${fx===fy?65:-65} ${ox+dx*8} ${oy+dy*15})`} />
        <circle cx={ox+dx*4} cy={oy+dy*4} r="5" fill={c} />
        <circle cx={ox+dx*4} cy={oy+dy*4} r="2.5" fill={c2} opacity="0.6" />
      </g>;
    })}
    {/* Side cartouches */}
    <ellipse cx="6" cy={h/2} rx="5" ry="14" fill="none" stroke={c3} strokeWidth="1" opacity="0.4" />
    <ellipse cx={w-6} cy={h/2} rx="5" ry="14" fill="none" stroke={c3} strokeWidth="1" opacity="0.4" />
    {/* Top center floral */}
    <circle cx={w/2} cy="6" r="3" fill={c2} opacity="0.3" />
  </>;
}

function BorderLevel8({ w, h }) {
  return <>
    <defs>
      <linearGradient id="goldShimmer" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#8d5524" />
        <stop offset="40%" stopColor="#ffd700" />
        <stop offset="60%" stopColor="#daa520" />
        <stop offset="100%" stopColor="#8d5524" />
        <animateTransform attributeName="gradientTransform" type="translate" from="-2 0" to="2 0" dur="2s" repeatCount="indefinite" />
      </linearGradient>
    </defs>
    <rect x="5" y="5" width={w-10} height={h-10} rx="4" fill="none" stroke="url(#goldShimmer)" strokeWidth="10" />
    <rect x="16" y="16" width={w-32} height={h-32} rx="3" fill="none" stroke="#daa520" strokeWidth="1.5" />
    <rect x="19" y="19" width={w-38} height={h-38} rx="2" fill="none" stroke="#8d5524" strokeWidth="1" />
    <rect x="22" y="22" width={w-44} height={h-44} rx="1" fill="none" stroke="#5c3a00" strokeWidth="0.5" />
    {/* Corner max acanthus clusters */}
    {[[0,0,1,1],[1,0,-1,1],[0,1,1,-1],[1,1,-1,-1]].map(([fx,fy,dx,dy],i) => {
      const ox = fx ? w-8 : 8, oy = fy ? h-8 : 8;
      return <g key={i}>
        {[0,1,2,3,4].map(j => {
          const ang = (fx===fy ? -15 : 15) + j * (fx===fy ? -18 : 18);
          return <ellipse key={j} cx={ox+dx*(8+j*3)} cy={oy+dy*(8+j*3)} rx="9" ry="4" fill={j%2===0 ? "#8d5524" : "#daa520"} opacity={0.3+j*0.08} transform={`rotate(${ang} ${ox+dx*(8+j*3)} ${oy+dy*(8+j*3)})`} />;
        })}
        <path d={`M ${ox} ${oy+dy*28} C ${ox+dx*12} ${oy+dy*28} ${ox+dx*12} ${oy} ${ox+dx*28} ${oy}`} fill="none" stroke="#ffd700" strokeWidth="1.5" opacity="0.5" />
        <circle cx={ox+dx*5} cy={oy+dy*5} r="6" fill="#daa520" />
        <circle cx={ox+dx*5} cy={oy+dy*5} r="4" fill="#ffd700" />
        <circle cx={ox+dx*5} cy={oy+dy*5} r="2" fill="#fff" opacity="0.5" />
      </g>;
    })}
    {/* Side cartouches */}
    {[h/2].map((cy) => <g key="sides">
      <ellipse cx="7" cy={cy} rx="6" ry="16" fill="none" stroke="#daa520" strokeWidth="1" opacity="0.4" />
      <ellipse cx="7" cy={cy-8} rx="3" ry="4" fill="#8d5524" opacity="0.3" />
      <ellipse cx="7" cy={cy+8} rx="3" ry="4" fill="#8d5524" opacity="0.3" />
      <ellipse cx={w-7} cy={cy} rx="6" ry="16" fill="none" stroke="#daa520" strokeWidth="1" opacity="0.4" />
      <ellipse cx={w-7} cy={cy-8} rx="3" ry="4" fill="#8d5524" opacity="0.3" />
      <ellipse cx={w-7} cy={cy+8} rx="3" ry="4" fill="#8d5524" opacity="0.3" />
    </g>)}
    {/* Top/bottom wreath motifs */}
    {[8, h-8].map((y, yi) => <g key={`wreath-${yi}`}>
      <path d={`M ${w/2-25} ${y} Q ${w/2} ${y+(yi===0?-6:6)} ${w/2+25} ${y}`} fill="none" stroke="#ffd700" strokeWidth="1.5" opacity="0.4" />
      <path d={`M ${w/2-18} ${y} Q ${w/2} ${y+(yi===0?-4:4)} ${w/2+18} ${y}`} fill="none" stroke="#daa520" strokeWidth="1" opacity="0.3" />
      <circle cx={w/2} cy={y} r="4" fill="#daa520" />
      <circle cx={w/2} cy={y} r="2" fill="#ffd700" />
    </g>)}
  </>;
}

function BorderLevel9({ w, h }) {
  return <>
    <defs>
      <linearGradient id="platShimmer" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#8080b0" />
        <stop offset="40%" stopColor="#c0c0e8" />
        <stop offset="60%" stopColor="#e8e8ff" />
        <stop offset="100%" stopColor="#8080b0" />
        <animateTransform attributeName="gradientTransform" type="translate" from="-2 0" to="2 0" dur="1.5s" repeatCount="indefinite" />
      </linearGradient>
    </defs>
    <rect x="5" y="5" width={w-10} height={h-10} rx="4" fill="none" stroke="url(#platShimmer)" strokeWidth="10" />
    <rect x="16" y="16" width={w-32} height={h-32} rx="3" fill="none" stroke="#c0c0e8" strokeWidth="1.5" />
    <rect x="19" y="19" width={w-38} height={h-38} rx="2" fill="none" stroke="#9e9e9e" strokeWidth="1" />
    <rect x="22" y="22" width={w-44} height={h-44} rx="1" fill="none" stroke="#6060a0" strokeWidth="0.5" />
    {/* Double acanthus clusters */}
    {[[0,0,1,1],[1,0,-1,1],[0,1,1,-1],[1,1,-1,-1]].map(([fx,fy,dx,dy],i) => {
      const ox = fx ? w-8 : 8, oy = fy ? h-8 : 8;
      return <g key={i}>
        {[0,1,2,3,4].map(j => <ellipse key={`i${j}`} cx={ox+dx*(8+j*3)} cy={oy+dy*(8+j*3)} rx="9" ry="4" fill={j%2===0 ? "#8080b0" : "#c0c0e8"} opacity={0.3+j*0.08} transform={`rotate(${(fx===fy ? -15 : 15) + j*(fx===fy?-18:18)} ${ox+dx*(8+j*3)} ${oy+dy*(8+j*3)})`} />)}
        {/* Outer halo ring */}
        {[0,1,2].map(j => <ellipse key={`o${j}`} cx={ox+dx*(20+j*4)} cy={oy+dy*(20+j*4)} rx="7" ry="3" fill="#6060a0" opacity="0.2" transform={`rotate(${j*40} ${ox+dx*(20+j*4)} ${oy+dy*(20+j*4)})`} />)}
        <path d={`M ${ox} ${oy+dy*30} C ${ox+dx*14} ${oy+dy*30} ${ox+dx*14} ${oy} ${ox+dx*30} ${oy}`} fill="none" stroke="#e8e8ff" strokeWidth="1.5" opacity="0.4" />
        <circle cx={ox+dx*5} cy={oy+dy*5} r="7" fill="#9e9e9e" />
        <circle cx={ox+dx*5} cy={oy+dy*5} r="5" fill="#c0c0e8" />
        <circle cx={ox+dx*5} cy={oy+dy*5} r="3" fill="#e8e8ff" />
        <circle cx={ox+dx*5} cy={oy+dy*5} r="1.5" fill="#fff" opacity="0.8" />
      </g>;
    })}
    {/* Side medallions with leaf spray */}
    {[h/2].map((cy) => <g key="sides">
      <ellipse cx="7" cy={cy} rx="6" ry="16" fill="none" stroke="#c0c0e8" strokeWidth="1" opacity="0.4" />
      <ellipse cx="7" cy={cy-10} rx="4" ry="5" fill="#8080b0" opacity="0.25" />
      <ellipse cx="7" cy={cy+10} rx="4" ry="5" fill="#8080b0" opacity="0.25" />
      <ellipse cx={w-7} cy={cy} rx="6" ry="16" fill="none" stroke="#c0c0e8" strokeWidth="1" opacity="0.4" />
      <ellipse cx={w-7} cy={cy-10} rx="4" ry="5" fill="#8080b0" opacity="0.25" />
      <ellipse cx={w-7} cy={cy+10} rx="4" ry="5" fill="#8080b0" opacity="0.25" />
    </g>)}
    {/* Top crown projection */}
    <g>
      <path d={`M ${w/2-28} 8 Q ${w/2} 2 ${w/2+28} 8`} fill="none" stroke="#e8e8ff" strokeWidth="1.5" opacity="0.4" />
      <path d={`M ${w/2-20} 8 Q ${w/2} 4 ${w/2+20} 8`} fill="none" stroke="#c0c0e8" strokeWidth="1" opacity="0.3" />
      {/* Crown peaks */}
      <path d={`M ${w/2-10} 6 L ${w/2-10} 2`} stroke="#e8e8ff" strokeWidth="1" opacity="0.4" />
      <path d={`M ${w/2} 4 L ${w/2} 0`} stroke="#e8e8ff" strokeWidth="1.5" opacity="0.5" />
      <path d={`M ${w/2+10} 6 L ${w/2+10} 2`} stroke="#e8e8ff" strokeWidth="1" opacity="0.4" />
      <circle cx={w/2} cy="8" r="4" fill="#c0c0e8" />
      <circle cx={w/2} cy="8" r="2" fill="#e8e8ff" />
    </g>
    {/* Bottom mirror */}
    <g>
      <path d={`M ${w/2-28} ${h-8} Q ${w/2} ${h-2} ${w/2+28} ${h-8}`} fill="none" stroke="#e8e8ff" strokeWidth="1.5" opacity="0.3" />
      <circle cx={w/2} cy={h-8} r="4" fill="#c0c0e8" />
      <circle cx={w/2} cy={h-8} r="2" fill="#e8e8ff" />
    </g>
  </>;
}

const BORDER_COMPONENTS = {
  1: BorderLevel1, 2: BorderLevel2, 3: BorderLevel3, 4: BorderLevel4,
  5: BorderLevel5, 6: BorderLevel6, 7: BorderLevel7, 8: BorderLevel8, 9: BorderLevel9,
};

export default function TieredPunchCard({ tier = 1, punches_earned = 0, punches_required = 10, business_name = "", reward = "", is_animating_level_up = false }) {
  const t = TIERS[tier] || TIERS[1];
  const W = 340, H = 200;
  const BorderComp = BORDER_COMPONENTS[tier] || BorderLevel1;

  return (
    <div
      className="w-full relative"
      style={{ aspectRatio: "1.7 / 1" }}
      data-testid="tiered-punch-card"
    >
      {is_animating_level_up && (
        <div className="absolute inset-0 z-10 animate-pulse rounded-xl" style={{ background: t.color, opacity: 0.1 }} />
      )}
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" className="rounded-xl overflow-visible">
        {/* Background */}
        <rect x="0" y="0" width={W} height={H} rx="8" fill={t.bg} />
        {/* Tier border */}
        <BorderComp w={W} h={H} />
        {/* Business name */}
        <text x={W/2} y="38" textAnchor="middle" fill={t.accent} fontSize="13" fontWeight="bold" fontFamily="system-ui">
          {business_name.length > 28 ? business_name.slice(0,26) + "..." : business_name}
        </text>
        {/* Tier label */}
        <text x={W/2} y="54" textAnchor="middle" fill={t.color} fontSize="9" opacity="0.6" fontFamily="system-ui">
          {t.name} Card
        </text>
        {/* Stamps */}
        <Stamps earned={punches_earned} required={punches_required} accent={t.accent} color={t.color} />
        {/* Reward text */}
        <text x={W/2} y={H-14} textAnchor="middle" fill={t.color} fontSize="9" opacity="0.5" fontFamily="system-ui">
          {reward.length > 35 ? reward.slice(0,33) + "..." : reward}
        </text>
      </svg>
    </div>
  );
}

export { TIERS };
